import { DiffReducer, JournalQueryResult, JournalState, ReduxAction, StateReducer } from '../common';
import { JournalActionTypes, initialJournalState } from '../store';
import { getJournalReducer } from '../store/journal.reducer';
import { clone, isExists } from './is.utils';
import { Diff, getDiff } from './rxjs.utils';

/**
 * Дифф-редьюсер, который срабатывает на изменение полей columnProperties и journalColumns и пересчитывает поле
 * displayingColumns
 */
export const displayingColumnsReducer: DiffReducer<JournalState, JournalActionTypes> = {
  fields: ['columnsProperties', 'journalColumns'],
  reducer(newState: JournalState): JournalState {
    const displayingColumns = newState.columnsProperties
      .map((dynamicInfo) => {
        const column = newState.journalColumns.find((col) => col.uniqueId === dynamicInfo.uniqueId);
        if (!isExists(column)) {
          return;
        }
        const newCol = clone(column);
        Object.assign(newCol, dynamicInfo);
        return newCol;
      })
      .filter(isExists);
    return { ...newState, displayingColumns };
  },
  ignoredActions: [],
};

export function combineReducers<T extends {}>(first: StateReducer<T>, second: StateReducer<T>): StateReducer<T> {
  return (state, action) => second(first(state, action), action);
}

export function diffMetaReducer<T extends object, A extends string>(
  diffReducers: DiffReducer<T, A>[],
  initialState: T,
  reducer: StateReducer<T>,
): (state: T, action: ReduxAction) => T {
  return (state: T = initialState, action: ReduxAction) =>
    diffReducers.reduce((newState: T, desc: DiffReducer<T, A>) => {
      if (desc.ignoredActions.indexOf(action.type as A) < 0) {
        const diff: Diff<T> = getDiff(state, newState, desc.fields);
        if (Object.keys(diff).length > 0) {
          return desc.reducer(newState, diff);
        }
      }
      return newState;
    }, reducer(state, action));
}

export function makeJournalReducer<T extends JournalState>(
  defaultQueryResult: JournalQueryResult,
  conf: { reducer?: StateReducer<T>; isDebug?: boolean } = {},
  initialState: T,
): StateReducer<T> {
  const initialDynamicData: T = isExists(initialState) ? initialState : (initialJournalState as T);

  const baseReducer = getJournalReducer<T>(defaultQueryResult) as StateReducer<T>;
  const resultReducer = isExists(conf.reducer) ? combineReducers(baseReducer, conf.reducer) : baseReducer;

  const diffReducer = diffMetaReducer(
    [displayingColumnsReducer as unknown as DiffReducer<T, JournalActionTypes>],
    initialDynamicData,
    resultReducer,
  );

  if (conf.isDebug) {
    return logMetaReducer(diffReducer);
  }
  return diffReducer;
}

function logMetaReducer<TState extends {}>(reducer: StateReducer<TState>): StateReducer<TState> {
  let i = 0;
  return (state: TState, action: ReduxAction) => {
    const newState = reducer(state, action);
    console.groupCollapsed(`Action ${++i}`);
    console.info(state);
    console.info(action);
    console.info(newState);
    console.groupEnd();
    return newState;
  };
}
